import Head from 'next/head'
import { NextSeo } from 'next-seo'

import * as Types from '../declarations'
import { withVersion } from 'lib/withVersion'
import { getDefaultImage } from 'lib/helper'

import PageTemplate from '../components/PageTemplate'
import EmailAddress from '../components/EmailAddress'

const Terms = ({ version }: { version: Types.VERSION }) => {
	const websiteURL = `https://www.guided${
		version === Types.VERSION.TREKKING ? 'trekking' : 'peaks'
	}.com`
	const websiteName = `Guided ${
		version === Types.VERSION.TREKKING ? 'Trekking' : 'Peaks'
	}`

	const seo = {
		title: `Terms - ${websiteName}`,
		description: `Review the terms and conditions for using our website. Learn about user responsibilities, policies, and guidelines for accessing our services.`,
		siteName: websiteName,
		canonical: `https://www.guided${
			version === Types.VERSION.TREKKING ? 'trekking' : 'peaks'
		}.com/terms`,
		images: [
			{
				url: getDefaultImage(version),
				type: 'image/jpeg',
			},
		],
	}

	return (
		<>
			<Head>
				<title>Terms - {websiteName}</title>
				<meta name="robots" content="noindex" />
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.images,
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<PageTemplate>
				<h1>Terms</h1>

				<p>
					Please read these Terms and Conditions (&quot;Terms&quot;, &quot;Terms
					and Conditions&quot;) carefully before using the {websiteURL} website
					(the &quot;Service&quot;) operated by {websiteName} (&quot;us&quot;,
					&quot;we&quot;, or &quot;our&quot;).
				</p>

				<h2>Acceptance of Terms</h2>
				<p>
					By accessing or using the Service, you agree to be bound by these
					Terms. If you disagree with any part of the terms, then you may not
					access the Service.
				</p>

				<h2>User Contributions</h2>
				<p>
					Users may post, upload, publish, submit, or transmit relevant,
					respectful content, including trekking routes, images, and comments
					(&quot;User Contributions&quot;). By making available any User
					Contributions on or through the Service, you hereby grant to{' '}
					{websiteName} a worldwide, irrevocable, perpetual, non-exclusive,
					transferable, royalty-free license, with the right to sublicense, to
					use, view, copy, adapt, modify, distribute, license, sell, transfer,
					publicly display, publicly perform, transmit, stream, broadcast,
					access, view, and otherwise exploit such User Contributions.
				</p>

				<h3>Copyright Policy</h3>
				<p>
					You represent and warrant that you own all rights in, or have obtained
					all necessary permissions to use, the User Contributions. You agree
					not to post, upload, or otherwise submit any User Contributions that
					violate another person&apos;s intellectual property rights.
				</p>
				<p>
					If you believe that your copyrighted work has been copied and posted
					on the Service in a way that constitutes copyright infringement,
					please provide us with the following information:
				</p>
				<ul>
					<li>
						A description of the copyrighted work that you claim has been
						infringed;
					</li>
					<li>
						A description of where the material that you claim is infringing is
						located on the Service;
					</li>
					<li>Your email address;</li>
					<li>
						A statement by you that you have a good faith belief that the
						disputed use is not authorized by the copyright owner, its agent, or
						the law;
					</li>
					<li>
						A statement by you, made under penalty of perjury, that the above
						information in your notice is accurate and that you are the
						copyright owner or authorized to act on the copyright owner&apos;s
						behalf.
					</li>
				</ul>
				<p>
					Please send the notice to <EmailAddress version={version} /> and we
					will take action ASAP.
				</p>

				<h3>Take Down Policy</h3>
				<p>
					We reserve the right to remove or disable access to any User
					Contributions that we believe violate these Terms or infringe any
					intellectual property rights, at our sole discretion, without prior
					notice to you.
				</p>
				{/* 
				<h2>Privacy Policy</h2>
				<p>
					Your privacy is important to us. Please review our Privacy Policy,
					which also governs your use of the Service, to understand our
					practices.
				</p> */}

				<h2>Changes</h2>
				<p>
					We reserve the right, at our sole discretion, to modify or replace
					these Terms at any time. We will provide notice of any changes by
					posting the new Terms on this page.
				</p>

				<h2>Contact Us</h2>
				<p>
					If you have any questions about these Terms, please contact us at{' '}
					<EmailAddress version={version} />
				</p>
			</PageTemplate>
		</>
	)
}

export const getStaticProps = withVersion(
	async (context: { version?: Types.VERSION }) => {
		return { props: { version: context?.version } }
	},
)

export default Terms
